import { useEffect } from 'react';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { UseFormReturnType } from '@mantine/form';
import { get, set } from 'lodash';

interface RichTextEditorInputProps {
  form: UseFormReturnType<any>;
  fieldName: string;
  placeholder: string;
  isDisabled?: boolean;
  height?: number | string;
  isMandatory?: boolean;
  allowBold?: boolean;
  allowItalic?: boolean;
  allowUnderline?: boolean;
  allowLink?: boolean;
  allowUnlink?: boolean;
  allowH1?: boolean;
  allowH2?: boolean;
  allowH3?: boolean;
  allowAlignLeft?: boolean;
  allowAlignCenter?: boolean;
  allowAlignRight?: boolean;
}

export default function RichTextEditorInput({
  form,
  fieldName,
  placeholder,
  isDisabled = false,
  height = 103,
  isMandatory = false,
  allowBold = true,
  allowItalic = true,
  allowUnderline = true,
  allowLink = true,
  allowUnlink = true,
  allowH1 = true,
  allowH2 = true,
  allowH3 = true,
  allowAlignLeft = true,
  allowAlignCenter = true,
  allowAlignRight = true,
}: RichTextEditorInputProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: get(form.values, fieldName) || '',
    editable: !isDisabled,
    onUpdate: ({ editor }: { editor: { getHTML: () => string } }) => {
      if (!isDisabled) {
        form.setValues((prev: Record<string, unknown>) => {
          const updated = { ...prev };
          set(updated, fieldName, editor.getHTML());
          return updated;
        });
      }
    },
  });

  const allowHeadings = allowH1 || allowH2 || allowH3;
  const allowAlignments = allowAlignLeft || allowAlignCenter || allowAlignRight;
  const allowTextStyles = allowBold || allowItalic || allowUnderline || allowLink || allowUnlink;
  const hasToolbar = allowHeadings || allowAlignments || allowTextStyles;

  useEffect(() => {
    const value = get(form.values, fieldName) || '';
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, get(form.values, fieldName)]);

  return (
    <div className="notes-form-group">
      <RichTextEditor
        editor={editor}
        w="100%"
        h={height}
        onBlur={() => isMandatory && form.validateField(fieldName)}
        className={isDisabled ? 'disabled' : ''}
      >
        {hasToolbar && (
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          {allowTextStyles && (
            <RichTextEditor.ControlsGroup>
              {allowBold && <RichTextEditor.Bold />}
              {allowItalic && <RichTextEditor.Italic />}
              {allowUnderline && <RichTextEditor.Underline />}
              {allowLink && <RichTextEditor.Link />}
              {allowUnlink && <RichTextEditor.Unlink />}
            </RichTextEditor.ControlsGroup>)}
          {allowHeadings && (
            <RichTextEditor.ControlsGroup>
              {allowH1 && <RichTextEditor.H1 />}
              {allowH2 && <RichTextEditor.H2 />}
              {allowH3 && <RichTextEditor.H3 />}
            </RichTextEditor.ControlsGroup>
          )}
          {allowAlignments && (
            <RichTextEditor.ControlsGroup>
              {allowAlignLeft && <RichTextEditor.AlignLeft />}
              {allowAlignCenter && <RichTextEditor.AlignCenter />}
              {allowAlignRight && <RichTextEditor.AlignRight />}
            </RichTextEditor.ControlsGroup>
          )}
        {/* </RichTextEditor.Toolbar> */}
         </RichTextEditor.Toolbar>)
        } 
        <RichTextEditor.Content className="rich-text-editor-content" />
      </RichTextEditor>
    </div>
  );
}
